import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Building2, LayoutGrid, ShoppingCart, ClipboardList, FileText, User2, LogOut, Home, ChevronRight } from "lucide-react";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";

const NAV = [
    { to: "/business/dashboard", icon: LayoutGrid, label: "Dashboard" },
    { to: "/business/shop", icon: Building2, label: "Wholesale shop" },
    { to: "/business/cart", icon: ShoppingCart, label: "Cart" },
    { to: "/business/orders", icon: ClipboardList, label: "Orders" },
    { to: "/business/invoices", icon: FileText, label: "Invoices" },
];

/**
 * Persistent shell for the /business/* portal — sidebar + top breadcrumb.
 * Loads the current b2b account and exposes it via render-prop `children(account)`.
 */
const B2BLayout = ({ title, subtitle, children, action, requireAccount = true }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [account, setAccount] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            try {
                const { data } = await api.get("/b2b/me");
                if (mounted) setAccount(data);
            } catch (err) {
                if (mounted) setAccount(null);
                if (requireAccount && err?.response?.status === 404) {
                    navigate("/business/register", { replace: true });
                }
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, [navigate, requireAccount]);

    return (
        <div className="min-h-screen bg-slate-50" data-testid="b2b-layout">
            <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/80 backdrop-blur-md">
                <div className="mx-auto flex max-w-7xl items-center gap-4 px-6 py-3">
                    <Link to="/business/dashboard" className="flex items-center gap-2 text-zinc-900 hover:text-brand-700">
                        <img src="/pills-logo.png" alt="PILLS" className="h-9 w-auto object-contain" />
                        <span className="rounded-md bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-zinc-500">For Business</span>
                    </Link>
                    {account?.legal_name && (
                        <span className="hidden truncate text-xs text-zinc-500 md:inline">
                            <ChevronRight className="-mt-0.5 inline h-3 w-3" /> {account.legal_name}
                        </span>
                    )}
                    <div className="ml-auto flex items-center gap-2">
                        <Link to="/" className="rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-700 hover:bg-slate-50" data-testid="b2b-back-consumer">
                            <Home className="-mt-0.5 mr-1 inline h-3 w-3" /> Consumer site
                        </Link>
                        <button
                            onClick={() => { logout(); navigate("/"); }}
                            className="rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-700 hover:bg-slate-50"
                            data-testid="b2b-logout-btn"
                        >
                            <LogOut className="-mt-0.5 mr-1 inline h-3 w-3" /> Sign out
                        </button>
                    </div>
                </div>
            </header>

            <div className="mx-auto grid max-w-7xl gap-6 px-6 py-6 lg:grid-cols-[220px_1fr]">
                <aside className="hidden lg:block">
                    <nav className="sticky top-20 space-y-0.5 rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
                        {NAV.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                className={({ isActive }) =>
                                    `flex items-center gap-2 rounded-xl px-3 py-2 text-sm transition-colors ${
                                        isActive ? "bg-brand-700 text-white shadow-sm" : "text-slate-700 hover:bg-slate-50"
                                    }`
                                }
                                data-testid={`b2b-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                                <n.icon className="h-4 w-4" /> {n.label}
                            </NavLink>
                        ))}
                        <NavLink
                            to="/business/account"
                            className={({ isActive }) =>
                                `mt-3 flex items-center gap-2 rounded-xl px-3 py-2 text-sm transition-colors ${
                                    isActive ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50"
                                }`
                            }
                            data-testid="b2b-nav-account"
                        >
                            <User2 className="h-4 w-4" /> Account
                        </NavLink>
                    </nav>
                </aside>

                <main className="min-w-0">
                    {(title || action) && (
                        <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
                            <div>
                                {subtitle && <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{subtitle}</p>}
                                {title && <h1 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">{title}</h1>}
                            </div>
                            {action}
                        </header>
                    )}
                    {loading ? <div className="h-64 skeleton rounded-3xl" /> : children?.(account)}
                </main>
            </div>
        </div>
    );
};

export default B2BLayout;
